import { Fragment, useEffect, useRef, useState, type ReactNode } from 'react';
import { Icon } from '../../components/ui/Icon';
import type { AllegatoInChat } from '../../stores/assistente.store';
import { apriFoto } from './allegatiApi';
import { PAROLE_ALLEGATI, altFoto, pagineDelPdf } from './allegatiTesto';
import './allegati.css';

/**
 * Gli allegati nel fumetto della domanda e la foto grande (29/10/2026, fetta 2F «Allegati» della
 * Fase 2; os-planning/ai/fase-2f-allegati.md, «Interfaccia»). Sopra il testo, allineate a destra,
 * le miniature delle foto (72 px al computer, 80 al telefono: le misure le fa allegati.css), poi i
 * PDF come nella striscia, senza x. Entrano con la stessa tendina della domanda.
 *
 * Una foto si apre grande con un clic; un PDF no, e il cursore resta normale. Un allegato che MUNE
 * non ha letto è a metà opacità con «Non letto»; una foto senza miniatura (la sua riga non c'è più)
 * resta col nome e l'icona, e non si apre. Mentre un file finisce di caricarsi (Invia premuto
 * prima della fine) il pezzo ha la sua barra.
 */

function Barra({ quanto }: { quanto: number }) {
  return (
    <span className="alg-barra" aria-hidden="true">
      <span className="alg-barra-piena" style={{ transform: `scaleX(${quanto})` }} />
    </span>
  );
}

/** Un PDF, o una foto senza miniatura: l'icona, il nome su una riga, sotto le pagine o lo stato. */
function PezzoDellaDomanda({ a }: { a: AllegatoInChat }) {
  const foto = a.tipo === 'foto';
  const inViaggio = a.letto && a.avanzamento !== null;
  const sotto = !a.letto ? PAROLE_ALLEGATI.nonLetto
    : inViaggio ? (foto ? PAROLE_ALLEGATI.preparoFoto : PAROLE_ALLEGATI.caricoPdf)
    : foto ? null
    : pagineDelPdf(a.pagine);
  return (
    <div className="alg-allegato" data-tipo={a.tipo} data-letto={a.letto ? undefined : 'false'} title={a.nome}>
      <Icon name={foto ? 'image' : 'file'} size={16} className="alg-icona" />
      <span className="alg-testo">
        <span className="alg-nome">{a.nome}</span>
        {sotto && <span className="alg-sotto">{sotto}</span>}
      </span>
      {inViaggio && a.avanzamento !== null && <Barra quanto={a.avanzamento} />}
    </div>
  );
}

/**
 * Le foto e i PDF della domanda, sopra il fumetto del testo (che c'è solo se c'è del testo).
 * `onApri` apre la foto grande (FotoGrande): solo le foto lette e con la miniatura.
 *
 * `reazione` (29/10/2026, dalla revisione della 2F): il tondo della reazione di MUNE in una domanda
 * senza testo. Sta sull'angolo in basso a sinistra dell'ultimo allegato, che è l'ultimo a destra
 * dell'ultima riga, come stava sull'angolo del fumetto prima della 2F. L'ultimo allegato ha sempre
 * il suo contenitore (.alg-ultimo), così quando la reazione arriva niente si rimonta.
 */
export function AllegatiDomanda({ allegati, onApri, reazione }: {
  allegati: AllegatoInChat[];
  mobile: boolean;
  onApri: (a: AllegatoInChat) => void;
  reazione?: ReactNode;
}): JSX.Element | null {
  if (!allegati.length) return null;
  // Prima le foto, poi i PDF (che vanno a capo).
  const ordine = [...allegati.filter((a) => a.tipo === 'foto'), ...allegati.filter((a) => a.tipo === 'pdf')];
  const ultimo = ordine[ordine.length - 1];
  const pezzo = (a: AllegatoInChat) => (a.tipo === 'foto' && a.letto && a.anteprima ? (
    <button type="button" className="alg-mini" title={a.nome} onClick={() => onApri(a)}>
      <img className="alg-mini-foto" src={a.anteprima} alt={altFoto(a.nome)} draggable={false} />
      {a.avanzamento !== null && <Barra quanto={a.avanzamento} />}
    </button>
  ) : (
    <PezzoDellaDomanda a={a} />
  ));
  return (
    <div className="alg-domanda">
      {ordine.map((a) => (a === ultimo ? (
        <span key={a.chiave} className="alg-ultimo">
          {pezzo(a)}
          {reazione}
        </span>
      ) : (
        <Fragment key={a.chiave}>{pezzo(a)}</Fragment>
      )))}
    </div>
  );
}

/**
 * La foto grande, dentro il pannello. Al computer copre corpo e piede (non la pagina): sopra il
 * nome in grigio e la x «Chiudi (Esc)»; al telefono tutto il pannello, con «Chiudi» largo quanto
 * lo schermo sotto il pollice. Entra con la tendina verticale. Un clic fuori dalla foto la chiude;
 * Esc la chiude prima del pannello (il gestore di Esc del pannello guarda `.ast .alg-grande`).
 *
 * Una domanda di adesso ha già la foto intera (l'anteprima blob: è la foto ridisegnata). Una
 * conversazione riaperta ha solo la miniatura da 160 px: si mostra subito, allargata, e intanto
 * la foto intera arriva da GET /ai/allegati/:id col Bearer; il suo indirizzo si libera alla
 * chiusura.
 */
export function FotoGrande({ allegato, mobile, onChiudi }: {
  allegato: AllegatoInChat;
  mobile: boolean;
  onChiudi: () => void;
}): JSX.Element {
  const anteprima = allegato.anteprima;
  const intera = !!anteprima && anteprima.startsWith('blob:');
  const [arrivata, setArrivata] = useState<string | null>(null);
  const chiudi = useRef<HTMLButtonElement>(null);

  // Il fuoco sul tasto che chiude: con la tastiera si chiude subito, e al telefono non sale niente.
  // Al computer, chiusa la foto, torna dov'era (la miniatura cliccata); al telefono no: un campo
  // che riprende il fuoco farebbe salire la tastiera.
  useEffect(() => {
    const prima = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    chiudi.current?.focus({ preventScroll: true });
    return () => {
      if (!mobile && prima?.isConnected) prima.focus({ preventScroll: true });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (intera || !allegato.id) return;
    let viva = true;
    let indirizzo: string | null = null;
    apriFoto(allegato.id)
      .then((u) => {
        if (!viva) { URL.revokeObjectURL(u); return; }
        indirizzo = u;
        setArrivata(u);
      })
      // Non arriva (la riga svuotata, la rete): resta la miniatura.
      .catch(() => {});
    return () => {
      viva = false;
      if (indirizzo) URL.revokeObjectURL(indirizzo);
    };
  }, [intera, allegato.id]);

  const src = arrivata ?? anteprima;
  const provvisoria = !intera && !arrivata;
  return (
    <div
      className="alg-grande"
      role="dialog"
      aria-label={altFoto(allegato.nome)}
      onClick={(e) => {
        if ((e.target as HTMLElement).closest('.alg-grande-foto')) return;
        onChiudi();
      }}
    >
      <div className="alg-grande-hd">
        <span className="alg-grande-nome" title={allegato.nome}>{allegato.nome}</span>
        {!mobile && (
          <button
            ref={chiudi}
            type="button"
            className="btn btn-ghost btn-icon alg-grande-x"
            title={PAROLE_ALLEGATI.chiudiComputer}
            aria-label={PAROLE_ALLEGATI.chiudiComputer}
            onClick={(e) => { e.stopPropagation(); onChiudi(); }}
          >
            <Icon name="x" size={14} />
          </button>
        )}
      </div>
      <div className="alg-grande-corpo">
        {src && (
          <img
            className="alg-grande-foto"
            data-provvisoria={provvisoria ? 'true' : undefined}
            src={src}
            alt={altFoto(allegato.nome)}
            draggable={false}
          />
        )}
      </div>
      {mobile && (
        <button
          ref={chiudi}
          type="button"
          className="btn alg-grande-chiudi"
          onClick={(e) => { e.stopPropagation(); onChiudi(); }}
        >
          {PAROLE_ALLEGATI.chiudiTelefono}
        </button>
      )}
    </div>
  );
}
